import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import StudyPlanPrintLayout from '../../components/StudyPlanPrintLayout.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { usePrintPlan } from '../../hooks/usePrintPlan.js';
import { Link, RouteContext } from '../../router.jsx';

export default function StaffStudentDetail({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const studentId = params?.id;
  const [refresh, setRefresh] = React.useState(0);
  const { data: me } = useApi('/api/me');
  const { data, loading, error } = useApi(`/api/students/${studentId}`, [studentId, refresh]);
  const { printDate, handlePrint } = usePrintPlan();
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });
  const [saving, setSaving] = React.useState(false);

  if (!studentId) {
    return (
      <div className="page">
        <div className="alert error">Missing student id.</div>
      </div>
    );
  }

  const canEditPlan = !!me?.user && (me.user.chair_privilege === 1 || me.user.admin_privilege === 1 || me.user.edit_privilege === 1);

  const handleToggleBan = async () => {
    if (!data) return;
    const nextBan = data.ban === 1 ? 0 : 1;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/students/${studentId}`, {
        method: 'PUT',
        body: JSON.stringify({ ban: nextBan })
      });
      setRefresh((value) => value + 1);
      setMessage({ text: nextBan === 1 ? 'Student placed on hold.' : 'Student reactivated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update status.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page advisor-study-plan-page">
      <div className="page-header print-hide">
        <div>
          <h1>Student Profile</h1>
          <p className="subtle">Review profile details and study plan status.</p>
        </div>
        <div className="header-actions print-hide">
          <Button variant="secondary" type="button" onClick={handlePrint}>Print Study Plan</Button>
          <Button variant="secondary" type="button" onClick={() => navigate('/staff/students')}>Back to Students</Button>
          {canEditPlan ? <Link to={`/staff/students/${studentId}/plan`} className="btn btn-secondary">Edit Study Plan</Link> : null}
          <Link to={`/staff/students/${studentId}/edit`} className="btn btn-primary">Edit Profile</Link>
        </div>
      </div>

      {error ? <div className="alert error print-hide">{error}</div> : null}
      {message.text ? <div className={`${message.tone === 'error' ? 'alert error' : 'alert'} print-hide`}>{message.text}</div> : null}

      {loading || !data ? (
        <p className="subtle">Loading...</p>
      ) : (
        <>
          <StudyPlanPrintLayout
            plan={data}
            studentName={fullName(data) || '—'}
            cin={data.cin || '—'}
            campusEmail={data.username || '—'}
            printDate={printDate}
          />

          <div className="grid two print-hide">
            <Card title="Student Overview">
              <div className="summary-list">
                <div className="summary-row">
                  <span>Name</span>
                  <span>{fullName(data) || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>CIN</span>
                  <span>{data.cin}</span>
                </div>
                <div className="summary-row">
                  <span>Campus Email</span>
                  <span>{data.username || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Personal Email</span>
                  <span>{data.personal_email || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Start Term</span>
                  <span>{[data.start_semester, data.start_year].filter(Boolean).join(' ') || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Status</span>
                  <span>{data.ban === 1 ? <Badge tone="warning">Hold</Badge> : <Badge tone="success">Active</Badge>}</span>
                </div>
              </div>
            </Card>

            <Card title="Status Controls" className="print-hide">
              <div className="toggle-row">
                <div>
                  <div className="label">Hold Access</div>
                  <div className="subtle">Temporarily disable student access.</div>
                </div>
                <label className="switch">
                  <input type="checkbox" checked={data.ban === 1} onChange={handleToggleBan} disabled={saving} />
                  <span className="slider" />
                </label>
              </div>
            </Card>
          </div>

          <div className="grid two print-hide">
            <Card title="Core Categories">
              <div className="summary-list">
                <div className="summary-row">
                  <span>Category 1</span>
                  <span>{data.cat1 || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 1 Notes</span>
                  <span>{data.cat1note || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 2</span>
                  <span>{data.cat2 || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 2 Notes</span>
                  <span>{data.cat2note || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 3</span>
                  <span>{joinCourses([data.cat31, data.cat32]) || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 3 Notes</span>
                  <span>{data.cat3note || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 4</span>
                  <span>{joinCourses([data.cat41, data.cat42]) || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Category 4 Notes</span>
                  <span>{data.cat4note || '—'}</span>
                </div>
              </div>
            </Card>

            <Card title="Electives & Requirements">
              <div className="summary-list">
                <div className="summary-row">
                  <span>Electives</span>
                  <span>{joinCourses([data.ec1, data.ec2, data.ec3, data.ec4, data.ec5, data.ec6]) || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Elective Notes</span>
                  <span>{data.ecnote || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Qual Needed</span>
                  <span>{data.qual_needed === 1 ? 'Yes' : 'No'}</span>
                </div>
                {data.qual_needed === 1 ? (
                  <div className="summary-row">
                    <span>Qualification Courses</span>
                    <span>{joinCourses([data.qc1, data.qc2, data.qc3, data.qc4, data.qc5]) || '—'}</span>
                  </div>
                ) : null}
                <div className="summary-row">
                  <span>Prereq Needed</span>
                  <span>{data.prereq_needed === 1 ? 'Yes' : 'No'}</span>
                </div>
                {data.prereq_needed === 1 ? (
                  <div className="summary-row">
                    <span>Prerequisite Courses</span>
                    <span>{joinCourses([data.pc1, data.pc2, data.pc3, data.pc4, data.pc5]) || '—'}</span>
                  </div>
                ) : null}
                <div className="summary-row">
                  <span>WPE Required</span>
                  <span>{data.wpe === 1 ? 'Yes' : 'No'}</span>
                </div>
                <div className="summary-row">
                  <span>Completion Options</span>
                  <span>{renderCompletion(data)}</span>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function fullName(data) {
  return `${data.first_name || ''} ${data.last_name || ''}`.trim();
}

function joinCourses(list) {
  return list
    .map((item) => (item || '').trim())
    .filter(Boolean)
    .join(', ');
}

function renderCompletion(data) {
  const items = [];
  if (data.comp === 1) items.push('Comprehensive Exam');
  if (data.thesis === 1) items.push('Thesis');
  if (items.length === 0) return '—';
  return items.join(', ');
}
